import React from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import { Button, TextField, SnackBar } from '@orijinworks/frontend-commons';
import { useServiceConfigurationHook } from './hooks/useServiceConfigurationHook';
import { ServiceFormModal } from './components/ServiceFormModal';
import { useServiceConfigurationStyles } from './service-configuration.style';

const ServiceConfiguration = () => {
  const classes = useServiceConfigurationStyles();
  const {
    closeModal,
    editingService,
    error,
    filteredServices,
    handleCloseToast,
    handleSaved,
    handleSystemCreated,
    loading,
    modalOpen,
    openCreate,
    openEdit,
    search,
    setSearch,
    systems,
    toast,
  } = useServiceConfigurationHook();

  return (
    <Box className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        Service Configuration
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        Create and modify service catalog entries. Creating a service also provisions its Strapi CMS record.
      </Typography>

      <Box className={classes.toolbar}>
        <TextField
          className={classes.searchField}
          placeholder="Search by name, display name, CMS key, or system"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          variant="outlined"
          size="small"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box className={classes.spacer} />
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreate}>
          New Service
        </Button>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Typography color="error" className={classes.empty}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <Box className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service name</TableCell>
                <TableCell>Display name</TableCell>
                <TableCell>System</TableCell>
                <TableCell>CMS key</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredServices.map((service) => (
                <TableRow key={service.serviceId}>
                  <TableCell>{service.serviceName}</TableCell>
                  <TableCell>{service.displayName || '—'}</TableCell>
                  <TableCell>{service.systemName || service.systemKey}</TableCell>
                  <TableCell>{service.cmsKey || '—'}</TableCell>
                  <TableCell>
                    {service.active ? 'Active' : <span className={classes.inactiveChip}>Inactive</span>}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" aria-label={`edit ${service.serviceName}`} onClick={() => openEdit(service)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filteredServices.length === 0 && <Typography className={classes.empty}>No services found.</Typography>}
        </Box>
      )}

      <ServiceFormModal
        open={modalOpen}
        service={editingService}
        systems={systems}
        onClose={closeModal}
        onSaved={handleSaved}
        onSystemCreated={handleSystemCreated}
      />

      {toast && (
        <Snackbar
          open
          autoHideDuration={2800}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <SnackBar
            isShown
            type={toast.type === 'error' || toast.type === 'success' ? toast.type : 'info'}
            onClose={handleCloseToast}
          >
            {toast.message}
          </SnackBar>
        </Snackbar>
      )}
    </Box>
  );
};

export default ServiceConfiguration;
